import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deactivateUser,
  listUsersPage,
} from "@/api/admin-api";
import type { UserPublic } from "@/api/auth-api";
import type { Role } from "@/store/auth-store";
import { useAuthStore } from "@/store/auth-store";
import { useToast } from "@/hooks/use-toast";
import { AppDialog } from "@/components/ui/AppDialog";

import { EditUserModal } from "./EditUserModal";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { Pagination } from "./Pagination";
import { pageSizeFor } from "@/lib/paging";
import { formatDateDMY } from "@/utils/dateFormat";
import {
  ALL_ROLES,
  ROLE_META,
  formatRelativeTime,
} from "./lookups";

const USERS_KEY = ["admin", "users"] as const;

type ActiveFilter = "all" | "active" | "inactive";

interface StatCard {
  label: string;
  value: number;
  cy: string;
}

export function UserTable() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const me = useAuthStore((s) => s.user);

  // Staff and assessment candidates are different populations doing
  // different jobs: one is the clinic, the other is people applying to
  // join it. Mixed in one table a hiring round buries the rota, and the
  // stats above ("Sonographers", "Radiologists") count a group that has
  // nothing to do with either.
  const [tab, setTab] = useState<"staff" | "candidates">("staff");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeFor("staff"));
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<Role | "all">("all");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [editTarget, setEditTarget] = useState<UserPublic | null>(null);
  const [resetTarget, setResetTarget] = useState<UserPublic | null>(null);

  // Active/Inactive stays client-side: it is a cheap narrowing of rows
  // already fetched, and refetching for it would blank the table on a
  // toggle. Everything else is in the key, or a search would be served
  // the previous query's cached page.
  const usersQuery = useQuery({
    queryKey: [
      ...USERS_KEY,
      { tab, search: search.trim(), roleFilter, page, pageSize },
    ] as const,
    queryFn: () =>
      listUsersPage({
        includeInactive: true,
        population: tab,
        ...(search.trim() ? { q: search.trim() } : {}),
        ...(roleFilter !== "all" ? { role: roleFilter } : {}),
        page,
        pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      toast({ title: "User deactivated", variant: "success" });
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Failed to deactivate";
      toast({ title: "Deactivate failed", description: detail, variant: "error" });
    },
  });

  const pageData = usersQuery.data;
  const users = pageData?.items ?? [];
  const total = pageData?.total ?? 0;
  const candidateCount = pageData?.candidate_total ?? 0;
  const staffCount = pageData?.staff_total ?? 0;

  const stats = useMemo<StatCard[]>(() => {
    // From the SERVER, not from `users`. Counted off the page these
    // would silently become "…on this page", which is the failure paging
    // introduces and nobody notices.
    if (tab === "candidates") {
      return [
        { label: "Candidates", value: candidateCount, cy: "stat-total" },
        {
          label: "Active",
          value: pageData?.active_total ?? 0,
          cy: "stat-active",
        },
        {
          label: "With an expiry",
          value: pageData?.expiring_total ?? 0,
          cy: "stat-expiring",
        },
      ];
    }
    return [
      { label: "Total", value: staffCount, cy: "stat-total" },
      { label: "Active", value: pageData?.active_total ?? 0, cy: "stat-active" },
      {
        label: "Sonographers",
        value: pageData?.sonographer_total ?? 0,
        cy: "stat-sonographers",
      },
      {
        label: "Radiologists",
        value: pageData?.radiologist_total ?? 0,
        cy: "stat-radiologists",
      },
    ];
  }, [tab, pageData, staffCount, candidateCount]);

  // Only the active/inactive chip is left client-side; search and role
  // moved into SQL when the list started paging, because otherwise they
  // would search the twenty rows on screen.
  const visible = useMemo(
    () =>
      users.filter((u) => {
        if (activeFilter === "active" && !u.active) return false;
        if (activeFilter === "inactive" && u.active) return false;
        return true;
      }),
    [users, activeFilter],
  );

  // The row awaiting confirmation. Holding the USER, not a boolean, so
  // the dialog can name who is about to lose access -- "Deactivate this
  // user?" over a table of twenty is not a question anybody can answer.
  const [deactivating, setDeactivating] = useState<UserPublic | null>(null);

  const onConfirmDeactivate = (u: UserPublic) => {
    if (me?.id === u.id) return; // guarded by disabled too
    setDeactivating(u);
  };

  return (
    <section data-cy="user-table-section" className="flex flex-col gap-4">
      {/* Stats bar */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-cy="stats-bar">
        {stats.map((s) => (
          <div
            key={s.label}
            data-cy={s.cy}
            className="rounded-xl border border-stat-card-border bg-stat-card px-4 py-3 shadow-theme-sm"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">
              {s.label}
            </div>
            <div className="mt-1 text-2xl font-bold text-foreground">
              {usersQuery.isLoading ? "—" : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Population tabs. Staff first because that is the everyday use;
          candidates are a hiring round, which is occasional. */}
      <div className="flex gap-1 border-b border-border" data-cy="user-tabs">
        {(
          [
            ["staff", `Staff (${staffCount})`],
            ["candidates", `Test candidates (${candidateCount})`],
          ] as ["staff" | "candidates", string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-cy={`user-tab-${key}`}
            onClick={() => {
              setPage(1);
              setTab(key);
              // The role filter belongs to the population it was chosen
              // in; carrying it across shows an empty table.
              setRoleFilter("all");
            }}
            className={[
              "-mb-px border-b-2 px-4 py-2 text-sm transition-colors",
              tab === key
                ? "border-accent font-semibold text-accent"
                : "border-transparent text-foreground-muted hover:text-foreground",
            ].join(" ")}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div
        className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background-card px-4 py-3 shadow-theme-sm"
        data-cy="user-filters"
      >
        <input
          type="search"
          placeholder="Search name, username, email, licence…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          data-cy="search-users"
          className="sk-input min-w-[16rem] flex-1"
        />

        {/* Every candidate is a TESTRAD, so a role picker there offers a
            choice of one. */}
        {tab === "staff" && (
        <label className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
          Role
          <select
            data-cy="filter-role"
            value={roleFilter}
            onChange={(e) => {
              setPage(1);
              setRoleFilter(
                e.target.value === "all" ? "all" : (e.target.value as Role),
              );
            }}
            className="sk-input sk-sm ml-2 w-auto"
          >
            <option value="all">All roles</option>
            {ALL_ROLES.filter((r) => r !== "TESTRAD").map((r) => (
              <option key={r} value={r}>
                {ROLE_META[r].label}
              </option>
            ))}
          </select>
        </label>
        )}

        <div
          className="flex gap-1 rounded-lg border border-input-border bg-input-bg p-0.5 text-xs"
          data-cy="filter-active"
          role="tablist"
        >
          {(["all", "active", "inactive"] as const).map((v) => (
            <button
              key={v}
              type="button"
              data-cy={`filter-active-${v}`}
              onClick={() => setActiveFilter(v)}
              className={[
                "rounded px-2 py-1 capitalize transition-colors",
                activeFilter === v
                  ? "bg-accent text-accent-text"
                  : "text-foreground-muted hover:text-foreground",
              ].join(" ")}
            >
              {v}
            </button>
          ))}
        </div>

        <span className="ml-auto text-xs text-foreground-muted" data-cy="row-count">
          {visible.length} of {users.length}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-sm">
        <table className="w-full text-sm" data-cy="user-table">
          <thead className="border-b-2 border-border-strong bg-table-header text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-table-header-text">
            <tr>
              <th className="px-4 py-3">Full name</th>
              <th className="px-4 py-3">Username</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">
                {tab === "candidates" ? "Access ends" : "Licence"}
              </th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.isLoading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-10 text-center text-foreground-muted"
                  data-cy="user-table-loading"
                >
                  Loading users…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-10 text-center text-foreground-muted"
                  data-cy="user-table-empty"
                >
                  No users match these filters.
                </td>
              </tr>
            ) : (
              visible.map((u, idx) => {
                const meta = ROLE_META[u.role];
                const isSelf = me?.id === u.id;
                const baseRowBg =
                  idx % 2 === 0 ? "bg-table-row-bg" : "bg-table-row-alt";
                return (
                  <tr
                    key={u.id}
                    data-cy="user-row"
                    data-user-id={u.id}
                    className={`border-t border-table-border ${baseRowBg} hover:bg-table-row-hover`}
                  >
                    <td className="px-4 py-2.5 text-foreground">
                      {u.full_name ?? <span className="text-foreground-muted">—</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-foreground">
                      {u.username}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-medium ${meta.badge}`}
                      >
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-foreground">
                      {tab === "candidates" ? (
                        u.access_expires_at ? (
                          <span
                            data-cy="candidate-expiry"
                            className={
                              new Date(u.access_expires_at) < new Date()
                                ? "text-status-urgent"
                                : "text-foreground"
                            }
                          >
                            {formatDateDMY(u.access_expires_at)}
                          </span>
                        ) : (
                          <span
                            className="text-foreground-muted"
                            title="This account never expires"
                          >
                            never
                          </span>
                        )
                      ) : (
                        u.license_number ?? (
                          <span className="text-foreground-muted">—</span>
                        )
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {u.active ? (
                        <span className="inline-flex items-center rounded-full bg-status-done-bg px-3 py-0.5 text-xs font-medium text-status-done">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-badge-reception-bg px-3 py-0.5 text-xs font-medium text-badge-reception">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-foreground-muted">
                      {formatRelativeTime(u.last_login_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          data-cy={`btn-edit-${u.id}`}
                          onClick={() => setEditTarget(u)}
                          className="sk-btn sk-sm"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          data-cy={`btn-reset-pwd-${u.id}`}
                          onClick={() => setResetTarget(u)}
                          className="rounded-md border border-status-wip/50 bg-status-wip-bg px-2 py-1 text-xs text-status-wip hover:bg-status-wip/20"
                        >
                          Reset pwd
                        </button>
                        <button
                          type="button"
                          data-cy={`btn-deactivate-${u.id}`}
                          disabled={isSelf || !u.active || deactivate.isPending}
                          title={
                            isSelf
                              ? "You cannot deactivate yourself"
                              : !u.active
                                ? "Already inactive"
                                : "Deactivate user"
                          }
                          onClick={() => onConfirmDeactivate(u)}
                          className="rounded-md border border-status-urgent/50 bg-status-urgent-bg px-2 py-1 text-xs text-status-urgent hover:bg-status-urgent/20 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        label={tab === "candidates" ? "candidates" : "staff"}
      />

      <EditUserModal
        open={editTarget !== null}
        user={editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      />
      <ResetPasswordModal
        open={resetTarget !== null}
        user={resetTarget}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
      />

      <AppDialog
        open={deactivating !== null}
        cy="dialog-deactivate"
        tone="danger"
        title="Deactivate this account?"
        body={
          deactivating
            ? `${deactivating.full_name ?? deactivating.username} loses access immediately, including any session they have open. The row stays in the system — audit entries name them, so the account is deactivated rather than deleted.`
            : undefined
        }
        onDismiss={() => setDeactivating(null)}
        actions={[
          {
            label: "Deactivate",
            cy: "btn-confirm-deactivate",
            variant: "danger",
            onClick: () => {
              const u = deactivating;
              setDeactivating(null);
              if (u) deactivate.mutate(u.id);
            },
          },
          {
            label: "Cancel",
            cy: "btn-cancel-deactivate",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setDeactivating(null),
          },
        ]}
      />
    </section>
  );
}

export { USERS_KEY };
